// src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { SubdomainProvider } from './contexts/SubdomainContext';
import { ColorPaletteProvider } from './contexts/ColorPaletteContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PageListPage from './pages/PageListPage';
import PageEditPage from './pages/PageEditPage';
import SettingsPage from './pages/SettingsPage';
import MediaPage from './pages/MediaPage';
import SubdomainRegistrationPage from './pages/SubdomainRegistrationPage';
import SubdomainManagementPage from './pages/SubdomainManagementPage';
import SubdomainSetupPage from './pages/SubdomainSetupPage';
import SubdomainRequiredRoute from './components/SubdomainRequiredRoute';
import PreviewPage from './pages/PreviewPage';
import PublicPage from './pages/PublicPage';
import LandingPage from './pages/LandingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import UserManagementPage from './pages/UserManagementPage';
import InvitationPage from './pages/InvitationPage';
import CommonPartsPage from './pages/CommonPartsPage';
import SubdomainPage from './pages/SubdomainPage';
import SubdomainRouter from './components/SubdomainRouter';
import AnalyticsScripts from './components/AnalyticsScripts';
import './styles/main.scss';

// 認証済みユーザーのみアクセス可能なルートをラップするコンポーネント
const PrivateRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return null;
  }

  return currentUser ? <>{children}</> : <Navigate to="/login" replace />;
};

// アカウントIDベースのルートをラップするコンポーネント
const AccountRoute = ({ children }) => {
  const { currentUser, accountId, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!accountId) {
    return <div>アカウント情報を読み込み中...</div>;
  }

  return <>{children}</>;
};

// メインのアプリケーションコンポーネント
const AppContent = () => {
  // サブドメインかどうかを判定
  const isSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // ローカル開発環境でのサブドメイン判定
    if (hostname.includes('localhost')) {
      // test01.localhost のような形式の場合
      if (parts.length > 1 && parts[parts.length - 1] === 'localhost') {
        return true;
      }
      // localhost の場合はURLパラメータで判定
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.has('subdomain');
    }

    // 127.0.0.1の場合はサブドメインではない
    if (hostname === '127.0.0.1') {
      return false;
    }

    // サブドメインがある場合（例: love.suisui.life）
    // suisui.lifeドメインの場合のみサブドメインとして扱う
    if (parts.length > 2 &&
      parts[parts.length - 2] === 'suisui' &&
      parts[parts.length - 1] === 'life') {
      return true;
    }

    return false;
  };

  // サブドメインの場合はSubdomainRouterを使用
  if (isSubdomain()) {
    return (
      <Router>
        <div className="app">
          <AnalyticsScripts />
          <SubdomainRouter />
        </div>
      </Router>
    );
  }

  // メインドメインの場合は通常のルーティング
  return (
    <Router>
      <div className="app">
        <Routes>
          {/* ログインページ */}
          <Route path="/login" element={<LoginPage />} />

          {/* プレビューページ用のルート - 新しいURL構造 */}
          <Route
            path="/preview/:accountId/:pageId"
            element={
              <AccountRoute>
                <div className="admin-layout">
                  <main>
                    <PreviewPage />
                  </main>
                </div>
              </AccountRoute>
            }
          />

          {/* サブドメイン設定ページ（サブドメイン必須化の入り口） */}
          <Route
            path="/subdomain-setup/:accountId"
            element={
              <AccountRoute>
                <SubdomainSetupPage />
              </AccountRoute>
            }
          />

          {/* 管理画面のルート - 新しいURL構造（サブドメイン必須） */}
          <Route
            path="/dashboard/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <DashboardPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />
          <Route
            path="/pages/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <PageListPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />
          <Route
            path="/pages/:accountId/new"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <PageEditPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />
          <Route
            path="/pages/:accountId/edit/:pageId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <PageEditPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />
          <Route
            path="/settings/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <SettingsPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />
          <Route
            path="/media/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <MediaPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />
          <Route
            path="/common-parts/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <CommonPartsPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />
          <Route
            path="/subdomain-registration/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <SubdomainRegistrationPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />

          {/* 管理者用：サブドメイン管理ページ */}
          <Route
            path="/subdomain-management/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <SubdomainManagementPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />

          {/* ユーザー管理ページ */}
          <Route
            path="/users/:accountId"
            element={
              <AccountRoute>
                <SubdomainRequiredRoute>
                  <div className="admin-layout">
                    <main>
                      <UserManagementPage />
                    </main>
                  </div>
                </SubdomainRequiredRoute>
              </AccountRoute>
            }
          />

          {/* 利用規約ページ */}
          <Route
            path="/terms-of-service"
            element={
              <>
                <AnalyticsScripts />
                <TermsOfServicePage />
              </>
            }
          />

          {/* プライバシーポリシーページ */}
          <Route
            path="/privacy-policy"
            element={
              <>
                <AnalyticsScripts />
                <PrivacyPolicyPage />
              </>
            }
          />

          {/* 招待ページ */}
          <Route
            path="/invitation/:accountId/:invitationId"
            element={
              <>
                <AnalyticsScripts />
                <InvitationPage />
              </>
            }
          />

          {/* ルートページ - CMSランディングページ */}
          <Route
            path="/"
            element={
              <>
                <AnalyticsScripts />
                <LandingPage />
              </>
            }
          />

          {/* 公開ページ用のルート - 認証不要 (最後に配置) */}
          <Route
            path="/:slug"
            element={
              <>
                <AnalyticsScripts />
                <PublicPage />
              </>
            }
          />

          {/* どのルートにもマッチしない場合 */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <SubdomainProvider>
          <TenantProvider>
            <SettingsProvider>
              <ColorPaletteProvider>
                <AppContent />
              </ColorPaletteProvider>
            </SettingsProvider>
          </TenantProvider>
        </SubdomainProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;